<template>
  <div class="audit-log-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.audit.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.audit.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" :disabled="exportingCsv" @click="handleExportCsv">
          <span v-if="exportingCsv" class="spinner spinner-sm"></span>
          {{ $t('admin.audit.btn_export') }}
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <input
        v-model="filters.date_from"
        type="date"
        class="filter-input"
        @change="fetchLogs(1)"
      >
      <span class="filter-separator">{{ $t('common.to') }}</span>
      <input
        v-model="filters.date_to"
        type="date"
        class="filter-input"
        @change="fetchLogs(1)"
      >

      <select v-model="filters.admin_user_id" class="filter-select" @change="fetchLogs(1)">
        <option value="">{{ $t('admin.audit.filter_all_admins') }}</option>
        <option v-for="admin in adminUsers" :key="admin.id" :value="admin.id">
          {{ admin.name }}
        </option>
      </select>

      <select v-model="filters.target_type" class="filter-select" @change="fetchLogs(1)">
        <option value="">{{ $t('admin.audit.filter_all_targets') }}</option>
        <option value="player">{{ $t('admin.audit.target_player') }}</option>
        <option value="game">{{ $t('admin.audit.target_game') }}</option>
        <option value="bonus">{{ $t('admin.audit.target_bonus') }}</option>
        <option value="withdrawal">{{ $t('admin.audit.target_withdrawal') }}</option>
        <option value="admin">{{ $t('admin.audit.target_admin') }}</option>
        <option value="setting">{{ $t('admin.audit.target_setting') }}</option>
      </select>

      <div class="search-wrap">
        <input
          v-model="filters.action"
          type="text"
          class="filter-input search-input"
          :placeholder="$t('admin.audit.filter_search_placeholder')"
          @keyup.enter="fetchLogs(1)"
        >
        <button class="btn-search" @click="fetchLogs(1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Log Table -->
    <div class="table-container">
      <div v-if="loading" class="table-loading">
        <span class="spinner"></span>
        {{ $t('admin.audit.loading') }}
      </div>

      <template v-else>
        <div class="table-header-bar">
          <span class="entry-count">{{ $t('admin.audit.total_entries', { n: meta.total.toLocaleString() }) }}</span>
        </div>

        <table>
          <thead>
            <tr>
              <th class="th-timestamp">{{ $t('admin.audit.col_timestamp') }}</th>
              <th class="th-admin">{{ $t('admin.audit.col_admin') }}</th>
              <th class="th-action">{{ $t('admin.audit.col_action') }}</th>
              <th class="th-target">{{ $t('admin.audit.col_target') }}</th>
              <th class="th-ip">{{ $t('admin.audit.col_ip') }}</th>
              <th class="th-details">{{ $t('admin.audit.col_details') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="logs.length === 0">
              <td colspan="6" class="empty-row">{{ $t('admin.audit.empty') }}</td>
            </tr>
            <template v-for="entry in logs" :key="entry.id">
              <tr class="log-row" :class="{ expanded: expandedId === entry.id }">
                <td class="td-timestamp">{{ formatTimestamp(entry.created_at) }}</td>
                <td class="td-admin-name">{{ entry.admin_name || $t('admin.audit.category_system') }}</td>
                <td>
                  <span class="badge" :class="actionBadgeClass(entry)">
                    {{ categoryLabel(entry) }}
                  </span>
                </td>
                <td class="td-target">
                  {{ formatTarget(entry) }}
                </td>
                <td class="td-ip">{{ entry.ip_address || '—' }}</td>
                <td>
                  <button
                    v-if="entry.before_data || entry.after_data"
                    class="expand-btn"
                    @click="toggleExpand(entry.id)"
                  >
                    {{ expandedId === entry.id ? $t('admin.audit.btn_hide') : $t('admin.audit.btn_show') }}
                  </button>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
              <tr v-if="expandedId === entry.id" class="detail-row">
                <td colspan="6">
                  <div class="detail-content">
                    <div v-if="entry.before_data" class="detail-section">
                      <div class="detail-label">{{ $t('admin.audit.detail_before') }}</div>
                      <div class="detail-json">
                        <template v-for="(value, key) in entry.before_data" :key="'before-' + key">
                          <div
                            class="json-line"
                            :class="{ removed: isChanged(key as string, entry.before_data, entry.after_data) }"
                          >
                            <span class="json-key">{{ key }}</span>: <span class="json-value">{{ formatJsonValue(value) }}</span>
                          </div>
                        </template>
                      </div>
                    </div>
                    <div v-if="entry.after_data" class="detail-section">
                      <div class="detail-label">{{ $t('admin.audit.detail_after') }}</div>
                      <div class="detail-json">
                        <template v-for="(value, key) in entry.after_data" :key="'after-' + key">
                          <div
                            class="json-line"
                            :class="{ added: isChanged(key as string, entry.before_data, entry.after_data) }"
                          >
                            <span class="json-key">{{ key }}</span>: <span class="json-value">{{ formatJsonValue(value) }}</span>
                          </div>
                        </template>
                      </div>
                    </div>
                    <div v-if="!entry.before_data && entry.after_data" class="detail-section detail-only-after">
                      <div class="detail-label">{{ $t('admin.audit.detail_details') }}</div>
                      <div class="detail-json">
                        <template v-for="(value, key) in entry.after_data" :key="'only-' + key">
                          <div class="json-line">
                            <span class="json-key">{{ key }}</span>: <span class="json-value">{{ formatJsonValue(value) }}</span>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <!-- Pagination -->
        <div v-if="meta.last_page > 1" class="pagination">
          <button class="pagination-btn" :disabled="meta.current_page <= 1" @click="fetchLogs(meta.current_page - 1)">&larr;</button>
          <button
            v-for="page in buildPageRange(meta.current_page, meta.last_page)"
            :key="page"
            class="pagination-btn"
            :class="{ active: page === meta.current_page }"
            @click="fetchLogs(page)"
          >
            {{ page }}
          </button>
          <span class="pagination-info">
            {{ (meta.current_page - 1) * meta.per_page + 1 }}-{{ Math.min(meta.current_page * meta.per_page, meta.total) }} of {{ meta.total.toLocaleString() }}
          </span>
          <button class="pagination-btn" :disabled="meta.current_page >= meta.last_page" @click="fetchLogs(meta.current_page + 1)">&rarr;</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()

// ==================== Types ====================

interface AuditLogEntry {
  id: number
  admin_name: string | null
  action: string
  target_type: string | null
  target_id: string | number | null
  before_data: Record<string, unknown> | null
  after_data: Record<string, unknown> | null
  ip_address: string | null
  created_at: string
}

interface AdminOption {
  id: number
  name: string
}

interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

// ==================== State ====================

const toast = useToastStore()

const logs = ref<AuditLogEntry[]>([])
const loading = ref(false)
const meta = ref<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 50, total: 0 })
const exportingCsv = ref(false)
const expandedId = ref<number | null>(null)

const adminUsers = ref<AdminOption[]>([])

const filters = ref({
  admin_user_id: '',
  action: '',
  target_type: '',
  date_from: '',
  date_to: '',
})

// ==================== Formatting Helpers ====================

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function categorizeAction(entry: AuditLogEntry): string {
  const action = entry.action.toLowerCase()
  if (action.includes('login') || action.includes('logout')) return 'login'
  if (action.includes('withdraw') || action.includes('deposit') || action.includes('wallet') || action.includes('credit') || action.includes('debit')) return 'finance'
  if (action.includes('player') || action.includes('suspend') || action.includes('ban') || action.includes('unsuspend') || action.includes('force_logout') || action.includes('reset_password') || action.includes('note')) return 'player'
  if (action.includes('setting') || action.includes('config')) return 'settings'
  if (action.includes('game') || action.includes('rtp')) return 'config'
  if (action.includes('role') || action.includes('admin') || action.includes('permission')) return 'admin'
  if (action.includes('bonus')) return 'finance'
  if (entry.target_type) {
    const type = entry.target_type.toLowerCase()
    if (type === 'player') return 'player'
    if (type === 'game') return 'config'
    if (type === 'withdrawal' || type === 'bonus') return 'finance'
    if (type === 'setting') return 'settings'
    if (type === 'admin') return 'admin'
  }
  return 'system'
}

function categoryLabel(entry: AuditLogEntry): string {
  const category = categorizeAction(entry)
  const map: Record<string, string> = {
    login: t('admin.audit.category_login'),
    finance: t('admin.audit.category_finance'),
    player: t('admin.audit.category_player'),
    settings: t('admin.audit.category_settings'),
    config: t('admin.audit.category_config'),
    admin: t('admin.audit.category_admin'),
    system: t('admin.audit.category_system'),
  }
  return map[category] || t('admin.audit.category_system')
}

function actionBadgeClass(entry: AuditLogEntry): string {
  const category = categorizeAction(entry)
  const map: Record<string, string> = {
    login: 'badge-login',
    finance: 'badge-finance',
    player: 'badge-player',
    settings: 'badge-settings',
    config: 'badge-config',
    admin: 'badge-admin',
    system: 'badge-system',
  }
  return map[category] || 'badge-system'
}

function formatTarget(entry: AuditLogEntry): string {
  const parts: string[] = []
  if (entry.action) parts.push(entry.action.replace(/_/g, ' '))
  if (entry.target_type && entry.target_id) {
    parts.push(`(${entry.target_type} #${entry.target_id})`)
  }
  return parts.join(' ') || '—'
}

function formatJsonValue(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function isChanged(key: string, before: Record<string, unknown> | null, after: Record<string, unknown> | null): boolean {
  if (!before || !after) return false
  if (!(key in before) || !(key in after)) return true
  return JSON.stringify(before[key]) !== JSON.stringify(after[key])
}

function buildPageRange(current: number, total: number): number[] {
  const pages: number[] = []
  const start = Math.max(1, current - 2)
  const end = Math.min(total, current + 2)
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
}

// ==================== Expand / Collapse ====================

function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id
}

// ==================== API ====================

async function fetchLogs(page: number) {
  loading.value = true
  try {
    const params: Record<string, string | number> = { page }
    if (filters.value.admin_user_id) params.admin_user_id = filters.value.admin_user_id
    if (filters.value.action) params.action = filters.value.action
    if (filters.value.target_type) params.target_type = filters.value.target_type
    if (filters.value.date_from) params.date_from = filters.value.date_from
    if (filters.value.date_to) params.date_to = filters.value.date_to

    const response = await apiClient.get('/admin/api/audit-logs', { params })
    logs.value = response.data.data
    meta.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
    }
  } catch {
    toast.error(t('admin.audit.error_load'))
  } finally {
    loading.value = false
  }
}

async function fetchAdminUsers() {
  try {
    const response = await apiClient.get('/admin/api/admin-users')
    adminUsers.value = (response.data.data || response.data).map((u: { id: number; name: string }) => ({
      id: u.id,
      name: u.name,
    }))
  } catch {
    // Non-critical — filter dropdown just won't show admin names
  }
}

async function handleExportCsv() {
  exportingCsv.value = true
  try {
    const params: Record<string, string | number> = {}
    if (filters.value.admin_user_id) params.admin_user_id = filters.value.admin_user_id
    if (filters.value.action) params.action = filters.value.action
    if (filters.value.target_type) params.target_type = filters.value.target_type
    if (filters.value.date_from) params.date_from = filters.value.date_from
    if (filters.value.date_to) params.date_to = filters.value.date_to

    const response = await apiClient.get('/admin/api/audit-logs/export', {
      params,
      responseType: 'blob',
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'audit-log-export.csv')
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    toast.success(t('admin.audit.success_export'))
  } catch {
    toast.error(t('admin.audit.error_export'))
  } finally {
    exportingCsv.value = false
  }
}

// ==================== Init ====================

onMounted(() => {
  fetchLogs(1)
  fetchAdminUsers()
})
</script>

<style scoped>
.audit-log-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 150ms ease;
  white-space: nowrap;
  border: none;
  font-family: inherit;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  background: #2a4455;
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Filters */
.filters-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filter-separator {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

.filter-input,
.filter-select {
  padding: 8px 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-family: inherit;
  min-width: 150px;
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: var(--accent-secondary, #1475e1);
}

.filter-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.search-wrap {
  display: flex;
  gap: 0;
}

.search-input {
  border-radius: 4px 0 0 4px;
  min-width: 180px;
}

.btn-search {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: none;
  border-radius: 0 4px 4px 0;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  transition: all 150ms ease;
}

.btn-search:hover {
  color: var(--text-primary, #fff);
  background: #2a4455;
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.table-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.entry-count {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 48px 16px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead th {
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  text-align: left;
  background: var(--bg-tertiary, #213743);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.th-timestamp { width: 180px; }
.th-admin { width: 140px; }
.th-action { width: 100px; }
.th-target { }
.th-ip { width: 130px; }
.th-details { width: 80px; }

tbody td {
  padding: 12px 16px;
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

tbody tr:last-child td {
  border-bottom: none;
}

.log-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.log-row.expanded {
  background: rgba(255, 255, 255, 0.02);
}

.empty-row {
  text-align: center;
  padding: 48px 16px !important;
  color: var(--text-tertiary, #7f8fa4);
}

.td-timestamp {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  white-space: nowrap;
}

.td-admin-name {
  color: var(--text-primary, #fff);
  font-weight: 700;
}

.td-target {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.td-ip {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4);
}

/* Badges */
.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge-login {
  background: rgba(20, 117, 225, 0.15);
  color: #1475e1;
}

.badge-finance {
  background: rgba(255, 215, 0, 0.15);
  color: #ffd700;
}

.badge-player {
  background: rgba(0, 231, 1, 0.15);
  color: #00e701;
}

.badge-settings {
  background: rgba(155, 89, 182, 0.15);
  color: #9b59b6;
}

.badge-config {
  background: rgba(255, 167, 38, 0.15);
  color: #ffa726;
}

.badge-admin {
  background: rgba(20, 117, 225, 0.15);
  color: #1475e1;
}

.badge-system {
  background: rgba(127, 143, 164, 0.15);
  color: #7f8fa4;
}

/* Expand Button */
.expand-btn {
  background: transparent;
  color: var(--accent-secondary, #1475e1);
  font-size: 12px;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 4px;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.expand-btn:hover {
  background: rgba(20, 117, 225, 0.15);
}

/* Detail Row */
.detail-row td {
  padding: 0 16px 16px !important;
  background: var(--bg-tertiary, #213743);
  border: none !important;
}

.detail-content {
  display: flex;
  gap: 16px;
}

.detail-section {
  flex: 1;
  background: var(--bg-primary, #0f1923);
  border-radius: 4px;
  padding: 12px;
  overflow: hidden;
}

.detail-only-after {
  display: none;
}

.detail-label {
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.detail-json {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  line-height: 1.6;
  color: var(--text-tertiary, #7f8fa4);
}

.json-line {
  padding: 1px 4px;
  border-radius: 2px;
}

.json-line.removed {
  background: rgba(237, 66, 69, 0.12);
  color: #ed4245;
}

.json-line.added {
  background: rgba(0, 231, 1, 0.12);
  color: #00e701;
}

.json-key {
  color: var(--text-secondary, #b1bad3);
  font-weight: 500;
}

.json-value {
  color: var(--text-primary, #fff);
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  justify-content: flex-end;
}

.pagination-btn {
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--bg-tertiary, #213743);
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 8px;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: var(--accent-primary, #00e701);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
